import {
  Avatar,
  Box,
  Button,
  capitalize,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { STRINGS_LOAD_MORE } from "../../../data/strings/Strings";
import { HeaderView } from "../../component/HeaderView";
import { OrdersViewModel } from "./OrdersViewModel";
import { LoadingView } from "../../component/LoadingView";
import { EmptyView } from "../../component/EmptyView";
import {
  provideDeliverOrderUseCase,
  provideDownloadMultipleOrderReceiptsUseCase,
  provideGetAllOrdersUseCase,
  provideGetAllTransactionsUseCase,
  provideUpdateOrderUseCase,
} from "../../di/UseCasesModule";
import { CustomTextView } from "../../component/CustomTextView";
import {
  COLOR_BLACK,
  COLOR_GREEN,
  COLOR_LESS_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_ORANGE,
  COLOR_RED,
  COLOR_VERY_LIGHT_GRAY,
  COLOR_WHITE,
  GRADIENT_MAIN,
} from "../../../data/colors/Colors";
import moment from "moment";
import { Link } from "react-router-dom";
import { ROUTE_ENDPOINTS } from "../../../data/constant/RouteConstants";
import { Order, OrderStatus } from "../../../domain/model/Order";
import { MAIN_FONT_FAMILY } from "../../../data/constant/Constants";
import { CustomImageView } from "../../component/CustomImageView";
import {
  CancelRounded,
  CheckBox,
  CheckCircleRounded,
  CheckRounded,
  CloseRounded,
  ContentCopyRounded,
  DownloadRounded,
  HomeRounded,
  LocalShippingRounded,
  OpenInNewRounded,
  PendingActions,
  PendingActionsRounded,
  PendingRounded,
  PhoneRounded,
} from "@mui/icons-material";
import React, { useState } from "react";
import { CustomButtonView } from "../../component/CustomButtonView";
import { useSnackbar } from "notistack";

export function OrdersView() {
  const viewModel = OrdersViewModel(
    provideGetAllOrdersUseCase(),
    provideUpdateOrderUseCase(),
    provideDownloadMultipleOrderReceiptsUseCase(),
    provideDeliverOrderUseCase()
  );

  const [selectedOrderForDelivery, setSelectedOrderForDelivery] =
    useState<Order | null>(null);
  const [isDeliveryModalOpen, setDeliveryModalOpen] = useState(false);

  const handleOpenDeliveryModal = (order: Order) => {
    setSelectedOrderForDelivery(order);
    setDeliveryModalOpen(true);
  };

  const handleCloseDeliveryModal = () => {
    setDeliveryModalOpen(false);
    setSelectedOrderForDelivery(null);
  };

  const handleConfirmDelivery = async () => {
    if (!selectedOrderForDelivery) return;
    const orderId = selectedOrderForDelivery._id;
    handleCloseDeliveryModal();
    await viewModel.deliverOrder(orderId);
  };

  return (
    <Stack spacing={2}>
      <HeaderView
        title={"Orders"}
        isLayoutFullWidthInSmallerScreens={false}
        layout={
          <Stack spacing={1} direction={"row"} justifyContent={"end"}>
            <Button
              onClick={viewModel.downloadMultipleOrderReceipts}
              variant="outlined"
              sx={{
                borderRadius: 22,
                borderColor: COLOR_BLACK,
                "&:hover": {
                  borderColor: COLOR_BLACK,
                },
              }}
            >
              <DownloadRounded fontSize="medium" sx={{ color: COLOR_BLACK }} />
              <CustomTextView
                text={`Download Receipts (${
                  viewModel.orders?.filter((o) => o.selected).length
                })`}
                fontWeight="bold"
                fontSize={16}
                props={{ textTransform: "none" }}
                textColor={COLOR_BLACK}
              />
            </Button>
          </Stack>
        }
      />
      <Container maxWidth={false}>
        {viewModel.isLoading && viewModel.orders?.length === 0 ? (
          <LoadingView />
        ) : (
          <>
            <Grid container spacing={2}>
              {viewModel.orders && viewModel.orders.length > 0 ? (
                <OrdersTableView
                  orders={viewModel.orders}
                  hasCustomer
                  onStatusChanged={(id, status) => {
                    viewModel.updateOrder(id, { status });
                  }}
                  onDeliverOrder={handleOpenDeliveryModal}
                  selectUnselectOrder={viewModel.selectUnselectOrder}
                />
              ) : (
                <EmptyView />
              )}
              <Grid
                alignItems={"center"}
                justifyContent={"center"}
                display={"flex"}
                size={12}
              >
                {viewModel.isLoading ? (
                  <LoadingView minHeight="5vh" />
                ) : (
                  <>
                    {viewModel.showLoadMoreButton && (
                      <Button
                        variant="outlined"
                        sx={{
                          borderRadius: 22,
                          textTransform: "none",
                          borderColor: COLOR_BLACK,
                          "&:hover": {
                            borderColor: COLOR_BLACK,
                            background: COLOR_LIGHT_GRAY,
                          },
                        }}
                        onClick={viewModel.getOrders}
                      >
                        <CustomTextView
                          text={STRINGS_LOAD_MORE}
                          variant="h6"
                          fontWeight="bold"
                        />
                      </Button>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </>
        )}
      </Container>

      {/* NBOX Delivery Dispatch Modal */}
      {selectedOrderForDelivery && (
        <DeliveryDispatchModal
          open={isDeliveryModalOpen}
          order={selectedOrderForDelivery}
          onClose={handleCloseDeliveryModal}
          onConfirm={handleConfirmDelivery}
        />
      )}
    </Stack>
  );
}

export function OrdersTableView({
  orders,
  hasCustomer,
  onStatusChanged,
  onDeliverOrder,
  selectUnselectOrder,
}: {
  orders: Array<Order>;
  hasCustomer: boolean;
  onStatusChanged: (orderId: string, status: OrderStatus) => void;
  onDeliverOrder?: (order: Order) => void;
  selectUnselectOrder: (orderId: string) => void;
}) {
  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 8,
        background: "transparent",
        border: "1px solid lightGray",
        boxShadow: 0,
      }}
    >
      <Table aria-label="simple table">
        <TableHead sx={{ background: COLOR_MAIN }}>
          <TableRow>
            <TableCell>
              <CustomTextView
                text="Id"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
              />
            </TableCell>
            {hasCustomer && (
              <TableCell>
                <CustomTextView
                  text="Customer"
                  fontWeight="bold"
                  fontSize={14}
                  textColor="white"
                />
              </TableCell>
            )}
            <TableCell>
              <CustomTextView
                text="Status"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
              />
            </TableCell>
            <TableCell align="center">
              <CustomTextView
                text="Delivery (NBOX)"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
                textAlign="center"
              />
            </TableCell>
            <TableCell>
              <CustomTextView
                text="Created"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
              />
            </TableCell>
            <TableCell>
              <CustomTextView
                text="Price"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
              />
            </TableCell>
            <TableCell>
              <CustomTextView
                text="Delivery Price"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
              />
            </TableCell>
            <TableCell align="center">
              <CustomTextView
                text="Quantity"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
                textAlign="center"
              />
            </TableCell>
            <TableCell align="center">
              <CustomTextView
                text="Product"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
                textAlign="center"
              />
            </TableCell>
            <TableCell align="center">
              <CustomTextView
                text="Details"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
                textAlign="center"
              />
            </TableCell>
            <TableCell align="center">
              <CustomTextView
                text="Selected"
                fontWeight="bold"
                fontSize={14}
                textColor="white"
                textAlign="center"
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order) => {
            const isConfirmed = order.status === OrderStatus.CONFIRMED;
            const hasDeliveryInfo = !!order.deliveryInfo?.trackingNumber;

            return (
              <TableRow
                key={order._id}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                }}
              >
                <TableCell>
                  <CustomTextView text={`#${order.orderId}`} fontSize={14} />
                </TableCell>
                {hasCustomer && (
                  <TableCell>
                    {order.customer && (
                      <Stack direction={"row"} alignItems={"center"} spacing={1}>
                        <Avatar
                          src={order.customer.profileImage}
                          sx={{ width: 60, height: 60 }}
                        />
                        <Stack>
                          <CustomTextView
                            text={order.customer.name}
                            fontSize={16}
                            fontWeight="bold"
                          />
                          <Link
                            to={
                              ROUTE_ENDPOINTS.HOME_CUSTOMERS +
                              "/" +
                              order.customer._id
                            }
                            target="_blank"
                          >
                            <CustomTextView
                              text={order.customer.user?.email}
                              fontSize={14}
                            />
                          </Link>
                        </Stack>
                      </Stack>
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <Select
                    sx={{
                      fontFamily: MAIN_FONT_FAMILY,
                      borderRadius: 4,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor:
                          order.status == OrderStatus.CONFIRMED
                            ? COLOR_GREEN
                            : order.status == OrderStatus.PENDING
                            ? COLOR_ORANGE
                            : COLOR_RED,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: COLOR_BLACK,
                      },
                      "& .MuiSvgIcon-root": {
                        color:
                          order.status == OrderStatus.CONFIRMED
                            ? COLOR_GREEN
                            : order.status == OrderStatus.PENDING
                            ? COLOR_ORANGE
                            : COLOR_RED,
                      },
                    }}
                    fullWidth
                    inputProps={{
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            background: COLOR_WHITE,
                          },
                        },
                      },
                    }}
                    value={order.status}
                    onChange={(e: SelectChangeEvent<string>) => {
                      if (e.target.value == order.status) return;
                      onStatusChanged(order._id, e.target.value as OrderStatus);
                    }}
                    renderValue={(val) => (
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        spacing={0.5}
                        width={"100%"}
                      >
                        {order.status == OrderStatus.CONFIRMED && (
                          <CheckCircleRounded
                            fontSize="small"
                            sx={{ color: COLOR_GREEN }}
                          />
                        )}
                        {order.status == OrderStatus.PENDING && (
                          <PendingActionsRounded
                            fontSize="small"
                            sx={{ color: "orange" }}
                          />
                        )}
                        {order.status == OrderStatus.CANCELLED && (
                          <CancelRounded fontSize="small" sx={{ color: "red" }} />
                        )}
                        <CustomTextView
                          text={capitalize(val as string)}
                          variant="body2"
                          textColor={
                            order.status === OrderStatus.CONFIRMED
                              ? COLOR_GREEN
                              : order.status === OrderStatus.PENDING
                              ? COLOR_ORANGE
                              : COLOR_RED
                          }
                        />
                      </Stack>
                    )}
                  >
                    {Object.values(OrderStatus).map((status, index) => {
                      return (
                        <MenuItem
                          key={index}
                          sx={{
                            fontFamily: MAIN_FONT_FAMILY,
                          }}
                          value={status}
                        >
                          <Stack
                            direction={"row"}
                            alignItems={"center"}
                            spacing={0.5}
                            width={"100%"}
                          >
                            {status == OrderStatus.CONFIRMED && (
                              <CheckCircleRounded
                                fontSize="small"
                                sx={{ color: COLOR_GREEN }}
                              />
                            )}
                            {status == OrderStatus.PENDING && (
                              <PendingActionsRounded
                                fontSize="small"
                                sx={{ color: "orange" }}
                              />
                            )}
                            {status == OrderStatus.CANCELLED && (
                              <CancelRounded
                                fontSize="small"
                                sx={{ color: "red" }}
                              />
                            )}
                            <CustomTextView
                              text={capitalize(status)}
                              variant="body2"
                            />
                          </Stack>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </TableCell>

                {/* NBOX Delivery Column */}
                <TableCell align="center">
                  {hasDeliveryInfo ? (
                    <Stack spacing={0.5} alignItems="center">
                      <Tooltip
                        title={
                          order.deliveryInfo?.waybillUrl
                            ? "Open NBOX Tracking / Waybill"
                            : "NBOX Tracking ID"
                        }
                        arrow
                      >
                        <Chip
                          icon={<LocalShippingRounded sx={{ fontSize: 16 }} />}
                          label={order.deliveryInfo?.trackingNumber}
                          size="small"
                          color="success"
                          variant="outlined"
                          onClick={() => {
                            const url =
                              order.deliveryInfo?.waybillUrl ||
                              `https://nbox.now/track/${order.deliveryInfo?.trackingNumber}`;
                            window.open(url, "_blank");
                          }}
                          sx={{
                            fontFamily: MAIN_FONT_FAMILY,
                            fontWeight: "bold",
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: "rgba(46, 125, 50, 0.08)",
                            },
                          }}
                        />
                      </Tooltip>
                      <CustomTextView
                        text={
                          order.deliveryInfo?.deliveryStatus
                            ? order.deliveryInfo.deliveryStatus.replace("_", " ")
                            : "pickup scheduled"
                        }
                        variant="caption"
                        textColor={COLOR_LIGHT_GRAY}
                      />
                    </Stack>
                  ) : (
                    <Tooltip
                      title={
                        isConfirmed
                          ? "Dispatch package to client with NBOX Logistics"
                          : "Order must be Confirmed before delivering to client"
                      }
                      arrow
                    >
                      <span>
                        <Button
                          variant="contained"
                          size="small"
                          disabled={!isConfirmed || !onDeliverOrder}
                          onClick={() => onDeliverOrder && onDeliverOrder(order)}
                          startIcon={<LocalShippingRounded />}
                          sx={{
                            textTransform: "none",
                            borderRadius: 3,
                            fontFamily: MAIN_FONT_FAMILY,
                            fontWeight: "bold",
                            backgroundColor: isConfirmed ? COLOR_MAIN : undefined,
                            "&:hover": {
                              backgroundColor: isConfirmed ? COLOR_LESS_BLACK : undefined,
                            },
                          }}
                        >
                          Deliver to Client
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>

                <TableCell>
                  <CustomTextView
                    text={moment(new Date(order.createdAt)).fromNow()}
                    fontSize={14}
                  />
                </TableCell>
                <TableCell>
                  <CustomTextView
                    text={`${(order.transaction.priceInQAR - order.transaction.deliveryPrice).toFixed(2)} QR`}
                    fontSize={16}
                    fontWeight="bold"
                  />
                </TableCell>
                <TableCell>
                  <CustomTextView
                    text={`${order.transaction.deliveryPrice.toFixed(2)} QR`}
                    fontSize={16}
                    fontWeight="bold"
                  />
                </TableCell>
                <TableCell align="center">
                  <CustomTextView
                    text={`${order.qty}`}
                    fontSize={18}
                    textAlign="center"
                    fontWeight="bold"
                  />
                </TableCell>
                <TableCell align="center">
                  {order.product ? (
                    <Stack alignItems={"center"} spacing={1}>
                      <CustomImageView
                        width={50}
                        height={50}
                        src={
                          (
                            order.product.imagesList![
                              order.product.thumbnailIndex!
                            ] as any
                          ).downloadUrl
                        }
                        scale="contain"
                        props={{ borderRadius: 4, background: COLOR_WHITE }}
                      />
                      <Link
                        to={
                          ROUTE_ENDPOINTS.HOME_PRODUCTS + "/" + order.product._id
                        }
                        target="_blank"
                      >
                        <CustomTextView
                          text={order.product.name}
                          fontSize={14}
                          textAlign="center"
                        />
                      </Link>
                    </Stack>
                  ) : (
                    <CustomTextView
                      text="REMOVED"
                      textColor="red"
                      textAlign="center"
                    />
                  )}
                </TableCell>

                <TableCell>
                  <Link
                    to={ROUTE_ENDPOINTS.HOME_ORDERS + "/" + order._id}
                    target="_blank"
                  >
                    <CustomTextView
                      text="Details"
                      textColor="blue"
                      textAlign="center"
                    />
                  </Link>
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    checked={order.selected}
                    onChange={() => {
                      selectUnselectOrder(order._id);
                    }}
                    sx={{
                      "&.Mui-checked": {
                        color: COLOR_MAIN,
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface DeliveryDispatchModalProps {
  open: boolean;
  order: Order;
  onClose: () => void;
  onConfirm: () => void;
}

function DeliveryDispatchModal({
  open,
  order,
  onClose,
  onConfirm,
}: DeliveryDispatchModalProps) {
  const customer = order.customer;
  const address = customer?.homeAddress;
  const formattedAddress = address
    ? `${address.country || "Qatar"}, Zone: ${address.zoneNumber || "-"}, Street: ${address.streetNumber || "-"}, Building: ${address.buildingNumber || "-"}, Floor: ${address.floorNumber || "-"}`
    : "No address recorded";

  const phoneNumber = customer?.phoneNumber
    ? `+${customer.phoneNumber.countryCode || "974"} ${customer.phoneNumber.number}`
    : "No phone recorded";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 5,
          p: 1,
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <LocalShippingRounded sx={{ color: COLOR_MAIN, fontSize: 28 }} />
            <CustomTextView
              text={`Dispatch Order #${order.orderId} to NBOX`}
              variant="h6"
              fontWeight="bold"
            />
          </Stack>
          <IconButton size="small" onClick={onClose}>
            <CloseRounded />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={2.5} pt={1}>
          {/* Customer / Recipient Info */}
          <Stack
            spacing={1.5}
            sx={{
              backgroundColor: COLOR_VERY_LIGHT_GRAY,
              p: 2,
              borderRadius: 3,
            }}
          >
            <CustomTextView
              text="Recipient Information"
              variant="subtitle2"
              fontWeight="bold"
              textColor={COLOR_MAIN}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar
                src={customer?.profileImage}
                sx={{ width: 36, height: 36 }}
              />
              <Stack>
                <CustomTextView
                  text={customer?.name || "Customer"}
                  variant="body2"
                  fontWeight="bold"
                />
                <CustomTextView
                  text={customer?.user?.email || ""}
                  variant="caption"
                  textColor={COLOR_LIGHT_GRAY}
                />
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <PhoneRounded sx={{ fontSize: 18, color: COLOR_MAIN }} />
              <CustomTextView text={phoneNumber} variant="body2" />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <HomeRounded
                sx={{ fontSize: 18, color: COLOR_MAIN, mt: 0.3 }}
              />
              <CustomTextView text={formattedAddress} variant="body2" />
            </Stack>
          </Stack>

          {/* Package / Order Details */}
          <Stack
            spacing={1.5}
            sx={{
              backgroundColor: "#f8fafc",
              border: "1px solid " + COLOR_VERY_LIGHT_GRAY,
              p: 2,
              borderRadius: 3,
            }}
          >
            <CustomTextView
              text="Package Details"
              variant="subtitle2"
              fontWeight="bold"
              textColor={COLOR_BLACK}
            />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <CustomTextView
                text={`Product: ${order.product?.name || "Product"}`}
                variant="body2"
              />
              <CustomTextView
                text={`Qty: ${order.qty}`}
                variant="body2"
                fontWeight="bold"
              />
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <CustomTextView text="Order Amount:" variant="body2" />
              <CustomTextView
                text={`${order.transaction?.priceInQAR?.toFixed(2) || "0.00"} QAR`}
                variant="body2"
                fontWeight="bold"
                textColor={COLOR_MAIN}
              />
            </Stack>
          </Stack>

          <CustomTextView
            text="Once confirmed, a shipment pickup request will be dispatched to NBOX Logistics and a tracking number will be assigned."
            variant="caption"
            textColor={COLOR_LIGHT_GRAY}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ pb: 2, px: 3 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderRadius: 3,
            textTransform: "none",
            borderColor: COLOR_LIGHT_GRAY,
            color: COLOR_BLACK,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          startIcon={<LocalShippingRounded />}
          sx={{
            borderRadius: 3,
            textTransform: "none",
            backgroundColor: COLOR_MAIN,
            fontWeight: "bold",
            "&:hover": {
              backgroundColor: COLOR_LESS_BLACK,
            },
          }}
        >
          Confirm & Dispatch
        </Button>
      </DialogActions>
    </Dialog>
  );
}
